import { makeAutoObservable } from 'mobx'
import type {
  AnalyticsChartPeriod,
  CallsAnalyticsKeys,
  ICallsChartResponse,
  ICallsStatisticResponse,
  ProcessedStatistic,
} from 'entities/Analytics'
import { formatDateForDisplay, calculateTrend, computeHasStatistic } from 'pages/analytics/lib'

export interface CallsRechartsDataPoint {
  date: string
  name: string
  total: number
  outbound_calls: number
  inbound_calls: number
  avg_duration_outbound: number
  answered_outbound: number
  unanswered_outbound: number
  busy_outbound: number
  failed_outbound: number
  avg_duration_inbound: number
  answered_inbound_human: number
  answered_inbound_ai: number
  missed_inbound: number
  busy_inbound: number
  failed_inbound: number
  forwarded: number
  avg_duration_forwarded: number
}

export class CallsChartDataStore {
  private _rawStatistic: ICallsStatisticResponse[] = []
  private _rawChartData: ICallsChartResponse[] = []
  private _period: AnalyticsChartPeriod = 'week'

  constructor() {
    makeAutoObservable(this)
  }

  setRawData = (
    response: { statistic: ICallsStatisticResponse[]; charts: ICallsChartResponse[] },
    period?: AnalyticsChartPeriod
  ) => {
    this._rawStatistic = response.statistic
    this._rawChartData = response.charts
    if (period) {
      this._period = period
    }
  }

  get processedStatistic(): ProcessedStatistic[] {
    return this._rawStatistic.map((item) => {
      const { key, value, prevValue } = item
      const trend = calculateTrend(value, prevValue)

      return {
        key: key,
        label: this.getDisplayLabel(key),
        value: value,
        prevValue: prevValue,
        trend,
      }
    })
  }

  get rechartsData(): CallsRechartsDataPoint[] {
    return this._rawChartData
      .map((item) => ({
        date: item.date,
        name: formatDateForDisplay(item.date, this._period),
        total: item.total || 0,
        outbound_calls: item.outbound_calls || 0,
        inbound_calls: item.inbound_calls || 0,
        avg_duration_outbound: item.avg_duration_outbound || 0,
        answered_outbound: item.answered_outbound || 0,
        unanswered_outbound: item.unanswered_outbound || 0,
        busy_outbound: item.busy_outbound || 0,
        failed_outbound: item.failed_outbound || 0,
        avg_duration_inbound: item.avg_duration_inbound || 0,
        answered_inbound_human: item.answered_inbound_human || 0,
        answered_inbound_ai: item.answered_inbound_ai || 0,
        missed_inbound: item.missed_inbound || 0,
        busy_inbound: item.busy_inbound || 0,
        failed_inbound: item.failed_inbound || 0,
        forwarded: item.forwarded || 0,
        avg_duration_forwarded: item.avg_duration_forwarded || 0,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }

  getStatisticByKey = (key: CallsAnalyticsKeys): ProcessedStatistic | undefined => {
    return this.processedStatistic.find((item) => item.key === key)
  }

  private getDisplayLabel = (key: CallsAnalyticsKeys): string => {
    const labelMap: Record<CallsAnalyticsKeys, string> = {
      total: 'Total calls',
      outbound_calls: 'Outbound calls',
      inbound_calls: 'Inbound calls',
      avg_duration_outbound: 'Avg. duration',
      answered_outbound: 'Answered',
      unanswered_outbound: 'Unanswered',
      busy_outbound: 'Busy',
      failed_outbound: 'Failed',
      avg_duration_inbound: 'Avg. duration',
      answered_inbound_human: 'Answered by human',
      answered_inbound_ai: 'Answered by AI',
      missed_inbound: 'Missed',
      busy_inbound: 'Busy',
      failed_inbound: 'Failed',
      forwarded: 'Forwarded',
      avg_duration_forwarded: 'Avg. duration',
    }
    return labelMap[key] || key
  }

  get hasStatistic() {
    return computeHasStatistic(this._rawStatistic)
  }

  dispose = () => {
    this._rawStatistic = []
    this._rawChartData = []
    this._period = 'week'
  }
}
